import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithReAuth } from '@/api/baseApi';
import {
  AudioAttachment,
  AutopsyAct,
  AutopsyActAudioPayload,
  AutopsyActBrief,
  AutopsyActCreatePayload,
  AutopsyActFilter,
  AutopsyActUpdatePayload,
} from '@/api/pathology/types';
import { vaccinationRegistryApi } from '@/api/vaccination/registry/api';
import { vaccinationRegistryTags } from '@/api/vaccination/registry/tags';
import { PageableRequest, PageableResponse } from '@/shared/api/types';
import {
  AUTOPSY_ACT,
  AUTOPSY_ACT_AUDIO,
  AUTOPSY_ACT_BY_ID,
  AUTOPSY_ACT_PROCESS,
  AUTOPSY_ACT_SIGN,
} from '@/shared/pathology/api';
import { mapSortToQueryParameter } from '@/utils/utils';

import { pathologyTags } from './tags';

const appendAudio = (formData: FormData, audio?: AudioAttachment) => {
  if (audio) {
    formData.append('audio', audio.file, audio.fileName);
  }
};

export const pathologyApi = createApi({
  reducerPath: 'pathologyApi',
  baseQuery: baseQueryWithReAuth,
  tagTypes: Object.values(pathologyTags),
  endpoints: (builder) => ({
    getAutopsyActs: builder.query<
      PageableResponse<AutopsyActBrief>,
      PageableRequest<AutopsyActFilter>
    >({
      query: (payload) => ({
        url: AUTOPSY_ACT,
        method: 'GET',
        params: {
          ...payload.filters,
          page: payload?.paging?.page,
          size: payload?.paging?.size,
          sort: mapSortToQueryParameter(payload?.sort),
        },
      }),
      providesTags: [pathologyTags.ACT_LIST],
    }),
    getAutopsyAct: builder.query<AutopsyAct, { id: string }>({
      query: ({ id }) => ({
        url: AUTOPSY_ACT_BY_ID(id),
        method: 'GET',
      }),
      providesTags: (_result, _error, { id }) => [{ type: pathologyTags.ACT, id }],
    }),
    // Часть data — JSON с типом application/json, audio — необязательный файл записи.
    // Content-Type не задаётся: для FormData браузер сам проставит boundary.
    createAutopsyAct: builder.mutation<AutopsyAct, AutopsyActCreatePayload>({
      query: ({ data, audio }) => {
        const formData = new FormData();
        formData.append(
          'data',
          new Blob([JSON.stringify(data)], { type: 'application/json' }),
        );
        appendAudio(formData, audio);

        return {
          url: AUTOPSY_ACT,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: [pathologyTags.ACT_LIST],
    }),
    updateAutopsyAct: builder.mutation<AutopsyAct, AutopsyActUpdatePayload>({
      query: ({ id, ...body }) => ({
        url: AUTOPSY_ACT_BY_ID(id),
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        pathologyTags.ACT_LIST,
        { type: pathologyTags.ACT, id },
      ],
    }),
    replaceAutopsyActAudio: builder.mutation<AutopsyAct, AutopsyActAudioPayload>({
      query: ({ id, audio }) => {
        const formData = new FormData();
        appendAudio(formData, audio);

        return {
          url: AUTOPSY_ACT_AUDIO(id),
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: (_result, _error, { id }) => [
        pathologyTags.ACT_LIST,
        { type: pathologyTags.ACT, id },
        { type: pathologyTags.ACT_AUDIO, id },
      ],
    }),
    processAutopsyAct: builder.mutation<void, { id: string }>({
      query: ({ id }) => ({
        url: AUTOPSY_ACT_PROCESS(id),
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { id }) => [
        pathologyTags.ACT_LIST,
        { type: pathologyTags.ACT, id },
      ],
    }),
    // После подписания особь переводится в статус «Падеж», поэтому журнал особей тоже устаревает
    signAutopsyAct: builder.mutation<AutopsyAct, { id: string }>({
      query: ({ id }) => ({
        url: AUTOPSY_ACT_SIGN(id),
        method: 'POST',
        body: {},
      }),
      invalidatesTags: (_result, _error, { id }) => [
        pathologyTags.ACT_LIST,
        { type: pathologyTags.ACT, id },
      ],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            vaccinationRegistryApi.util.invalidateTags([
              vaccinationRegistryTags.REGISTRY,
              vaccinationRegistryTags.REGISTRY_RECORD,
            ]),
          );
        } catch {
          // ошибка уже показана в baseQueryWithReAuth
        }
      },
    }),
    deleteAutopsyAct: builder.mutation<void, { id: string }>({
      query: ({ id }) => ({
        url: AUTOPSY_ACT_BY_ID(id),
        method: 'DELETE',
      }),
      invalidatesTags: [pathologyTags.ACT_LIST],
    }),
    getAutopsyActAudio: builder.query<Blob, { id: string }>({
      query: ({ id }) => ({
        url: AUTOPSY_ACT_AUDIO(id),
        method: 'GET',
        responseHandler: (response) => response.blob(),
      }),
      providesTags: (_result, _error, { id }) => [{ type: pathologyTags.ACT_AUDIO, id }],
      keepUnusedDataFor: 0,
    }),
  }),
});

export const {
  useGetAutopsyActsQuery,
  useGetAutopsyActQuery,
  useCreateAutopsyActMutation,
  useUpdateAutopsyActMutation,
  useReplaceAutopsyActAudioMutation,
  useProcessAutopsyActMutation,
  useSignAutopsyActMutation,
  useDeleteAutopsyActMutation,
  useGetAutopsyActAudioQuery,
} = pathologyApi;
